import React, { useMemo, useState } from "react";
import Grid from "@mui/material/Grid2";
import { ParsedPage, RepeatedLineCandidate, detectRepeatedLines } from "./PdfImporter.types";

interface RepeatedLinesBannerProps {
  pages: ParsedPage[];
  ignoredLines: Set<string>;
  removedLines: Set<string>;
  onUnignore: (lines: string[]) => void;
  onIgnore: (lines: string[]) => void;
  onAdd: (lines: string[]) => void;
  onRemove: (lines: string[]) => void;
}

/** A candidate is "removed" if every one of its raw lines is in the removed set, and it is "ignored" if in the ignored set. */
function isCandidateInSet(c: RepeatedLineCandidate, theSet: Set<string>): boolean {
  return c.lines.every(l => theSet.has(l));
}

export default function RepeatedLinesBanner({ pages, ignoredLines, removedLines, onIgnore, onUnignore, onAdd, onRemove }: RepeatedLinesBannerProps) {
  const [expanded, setExpanded] = useState(false);

  const candidates = useMemo(() => detectRepeatedLines(pages), [pages]);

  if (candidates.length === 0) return null;

  const ignoredCount = candidates.filter(c => isCandidateInSet(c, ignoredLines)).length;
  const removedCount = candidates.filter(c => isCandidateInSet(c, removedLines)).length;
  const allRemoved = removedCount === candidates.length;

  const handleAddAll = () => {
    const unignored = candidates.filter(o => !isCandidateInSet(o, ignoredLines));
    onAdd(unignored.flatMap(o => o.lines));
  }

  const handleRemoveAll = () => {
    debugger;
    const unignored = candidates.filter(o => !isCandidateInSet(o, ignoredLines));
    onRemove(unignored.flatMap(o => o.lines));
  }

  return (
    <div style={{ border: "1px solid #e0c060", borderRadius: "6px", background: "#fffbe8", padding: "0.5rem 0.75rem" }}>
      <Grid container spacing={1} alignItems="center">
        <Grid size="grow">
          <span style={{ fontSize: "0.85rem", fontWeight: "bold", color: "#7a5800" }}>
            Repeated Lines Detected
          </span>
          <span style={{ fontSize: "0.8rem", color: "#9a7010", marginLeft: "0.5rem" }}>
            {candidates.length} candidate{candidates.length !== 1 ? "s" : ""}
            {ignoredCount > 0 && ` · ${ignoredCount} ignored`}
            {removedCount > 0 && ` · ${removedCount} suppressed`}
          </span>
        </Grid>
        <Grid>
          <button
            onClick={() => allRemoved
              ? handleRemoveAll()
              : handleAddAll()
            }
            style={{ fontSize: "0.8rem", padding: "0.2rem 0.5rem", cursor: "pointer", borderRadius: "4px", border: "1px solid #c0a030" }}
          >
            {allRemoved ? "Restore All" : "Remove All"}
          </button>
        </Grid>
        <Grid>
          <button
            onClick={() => setExpanded(e => !e)}
            style={{ fontSize: "0.8rem", padding: "0.2rem 0.5rem", cursor: "pointer", borderRadius: "4px", border: "1px solid #c0a030", background: "none" }}
          >
            {expanded ? "▲ Hide" : "▼ Show"}
          </button>
        </Grid>
      </Grid>

      {expanded && (
        <div style={{ marginTop: "0.5rem", display: "flex", flexDirection: "column", gap: "2px" }}>
          {candidates.map(c => {
            const isIgnored = isCandidateInSet(c, ignoredLines);
            const isRemoved = isCandidateInSet(c, removedLines);
            const isIgnoredOrRemoved = isIgnored || isRemoved;
            return (
              <Grid
                key={c.template}
                container
                spacing={1}
                alignItems="center"
                style={{
                  padding: "0.25rem 0.4rem",
                  borderRadius: "4px",
                  background: isIgnoredOrRemoved ? "#fde8e8" : "#fff",
                  border: "1px solid",
                  borderColor: isIgnoredOrRemoved ? "#f0a0a0" : "#e8d890",
                }}
              >
                <Grid size="grow" style={{ minWidth: 0 }}>
                  <code style={{
                    fontSize: "0.8rem",
                    color: isIgnoredOrRemoved ? "#a00" : "#333",
                    textDecoration: isIgnoredOrRemoved ? "line-through" : "none",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                    display: "block",
                  }}>
                    {c.template}
                  </code>
                  {c.hasPageNumbers && (
                    <span style={{ fontSize: "0.7rem", color: "#888", fontStyle: "italic" }}>
                      includes page numbers · {c.lines.length} variants
                    </span>
                  )}
                </Grid>
                <Grid>
                  <span style={{ fontSize: "0.75rem", color: "#888", whiteSpace: "nowrap" }}>
                    {c.count}× ({Math.round(c.fraction * 100)}% of pages)
                  </span>
                </Grid>
                <Grid>
                  <button
                    onClick={() => isIgnored ? onIgnore(c.lines) : onUnignore(c.lines)}
                    style={{
                      fontSize: "0.75rem",
                      padding: "0.15rem 0.45rem",
                      cursor: "pointer",
                      borderRadius: "4px",
                      border: "1px solid",
                      borderColor: isIgnored ? "#4a90d9" : "#d04040",
                      color: isIgnored ? "#4a90d9" : "#d04040",
                      background: "none",
                    }}
                  >
                    {isIgnored ? "Unignore" : "Ignore"}
                  </button>
                </Grid>
                <Grid>
                  <button
                    onClick={() => isRemoved ? onRemove(c.lines) : onAdd(c.lines)}
                    style={{
                      fontSize: "0.75rem",
                      padding: "0.15rem 0.45rem",
                      cursor: "pointer",
                      borderRadius: "4px",
                      border: "1px solid",
                      borderColor: isRemoved ? "#4a90d9" : "#d04040",
                      color: isRemoved ? "#4a90d9" : "#d04040",
                      background: "none",
                    }}
                  >
                    {isRemoved ? "Restore" : "Remove"}
                  </button>
                </Grid>
              </Grid>
            );
          })}
        </div>
      )}
    </div>
  );
}